import moment from "moment";
import { DATE_FORMAT, LOOKUP_VALUE } from "./constant";
import { useDialog } from "../store/api/hooks/dialogHook";
import i18n from "../i18n";
import QRCode from "qrcode";
import { APP_DOMAIN, URLS } from "../_config";

export const getBusinessDisplayName = (obj: { dbaName?: string; companyName?: string } | null | undefined): string => {
    return obj?.dbaName || obj?.companyName || "";
};

// Maps the app's i18next language codes to BCP-47 tags for Intl.DateTimeFormat,
// since Intl doesn't understand a couple of this app's codes (e.g. bare 'zh')
// the same way a browser's default locale negotiation would.
const INTL_LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: "en-US",
  es: "es",
  zh: "zh-CN",
  ru: "ru",
  ko: "ko",
  "pt-BR": "pt-BR",
  fr: "fr",
};

export const getIntlLocale = (language?: string): string =>
  (language && INTL_LOCALE_BY_LANGUAGE[language]) || "en-US";

// moment().format() always renders day/month names in English -- this app never
// registers moment locale data or calls moment.locale(). Use this for any
// human-readable (weekday/month-name) date display instead of a raw moment
// format string, so it respects the active UI language. Callers still do their
// own UTC->local conversion (e.g. moment.utc(x).local().toDate()) before
// passing the resulting Date in here, same as they would before .format(...).
export const formatLocalizedDate = (
  date: Date,
  language: string | undefined,
  options: Intl.DateTimeFormatOptions
): string => date.toLocaleDateString(getIntlLocale(language), options);

// Native "AM"/"PM" text (e.g. custom time-picker segments, anywhere the literal
// day-period abbreviation is shown outside of a full formatted timestamp) --
// asks Intl for the real day-period string for the active language (e.g. 上午/
// 下午, 오전/오후, "a. m."/"p. m."), rather than hardcoding the English word.
// Same idea as formatLocalizedDate, for the "hh:mm A" half of a timestamp --
// composing `${formatLocalizedDate(d, language, {...})} ${formatLocalizedTime(d, language)}`
// replaces a single moment().format("MM/DD/YYYY hh:mm A")-style call.
export const formatLocalizedTime = (
  date: Date,
  language: string | undefined,
  options?: Intl.DateTimeFormatOptions
): string =>
  date.toLocaleTimeString(getIntlLocale(language), options || { hour: "2-digit", minute: "2-digit", hour12: true });

export const getPeriodLabel = (period: "AM" | "PM", language?: string): string => {
  const hour = period === "AM" ? 9 : 15;
  const parts = new Intl.DateTimeFormat(getIntlLocale(language), { hour: "numeric", hour12: true }).formatToParts(
    new Date(2000, 0, 1, hour, 0)
  );
  return parts.find((p) => p.type === "dayPeriod")?.value || period;
};

// Some backend endpoints (e.g. Dashboard/GetSalesTrendByHour) return pre-formatted
// hourly bucket labels like "12:00AM"/"01:00PM" as an opaque string, with no raw
// timestamp alongside them for the client to format itself. Parse that shape back
// into hour/minute and rebuild it through Intl so the day period localizes too.
// Falls back to the original label unchanged if it doesn't match the expected shape.
export const formatLocalizedHourLabel = (label: string, language?: string): string => {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(label?.trim() || "");
  if (!match) return label;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  const date = new Date(2000, 0, 1, hour, minute);
  return formatLocalizedTime(date, language, { hour: "numeric", minute: "2-digit", hour12: true });
};

export const safeAtob = (value: string): string => {
  try {
    return atob(value);
  } catch {
    return value;
  }
};

export const createURL = (url: string, params: any) => {
    Object.keys(params).map((param) => {
        url = url.replace(`:${param}`, params[param]);
    });
    return url;
};

export const UpdateWithVariables = (url: string, data: any) => {
    if (data) {
        let dynamicParams = url.match(/:[A-Z_]*/gi);
        dynamicParams?.map((key) => {
            if (key.length > 1) url = url.replace(key, data[key.replace(":", "")]);
        });
    }
    return url;
};

export function CheckNumber(e: { target: { value: string; getAttribute: (arg0: string) => string } }) {
    let n = e.target.value.replace(/^0+/, "").replace(/[^\d.]/g, "");
    const min: number = parseFloat(e.target.getAttribute("min"));
    const max: number = parseFloat(e.target.getAttribute("max"));
    if (n == "") {
        n = "0";
    }
    if (min && parseFloat(n) < min) {
        n = String(min);
    }

    if (max && parseFloat(n) > max) {
        n = String(max);
    }
    e.target.value = n;
}

export const showDate = (dbDate: any, dateFormat: any = DATE_FORMAT) => {
    return dbDate !== "" ? moment(dbDate).format(dateFormat) : "";
};
export const showLocalDate = (dbDate: any, dateFormat: any = DATE_FORMAT) => {
    return dbDate ? moment.utc(dbDate).local().format(dateFormat) : "-";
};

export const showLocalTime = (dbDate: any, dateFormat: any = DATE_FORMAT, language?: string) => {
    return dbDate ? formatLocalizedTime(moment.utc(dbDate).local().toDate(), language) : "-";
};

export const showLocalDateTime = (dbDate: any, dateFormat: any = DATE_FORMAT, language?: string) => {
    return dbDate
        ? moment.utc(dbDate).local().format(dateFormat) + " " + formatLocalizedTime(moment.utc(dbDate).local().toDate(), language)
        : "-";
};

export const localDate = (dbDate: any, dateFormat: any = DATE_FORMAT) => {
    return dbDate ? moment.utc(dbDate).local().toDate() : undefined;
};
export const localTime = (dbDate: any, dateFormat: any = DATE_FORMAT) => {
    return dbDate ? moment.utc(dbDate).local() : undefined;
};

/**
 * Convert a local date to UTC ISO string for API calls
 * @param date - The local date to convert
 * @returns UTC ISO string or current UTC time if date is null
 */
export const convertToUtcIsoString = (date: Date | null | undefined): string => {
    if (!date) {
        return moment().utc().toISOString();
    }
    return moment(date).utc().toISOString();
};

/**
 * Convert UTC date string from API to local Date object for display
 * @param utcDateString - UTC date string from API
 * @returns Local Date object or null if invalid
 */
export const convertFromUtcToLocal = (utcDateString: string | null | undefined): Date | null => {
    if (!utcDateString) {
        return null;
    }
    const localDate = moment.utc(utcDateString).local();
    return localDate.isValid() ? localDate.toDate() : null;
};

/**
 * Convert UTC date string from API to local Date object with fallback to current date
 * @param utcDateString - UTC date string from API
 * @returns Local Date object or current date if invalid
 */
export const convertFromUtcToLocalWithFallback = (utcDateString: string | null | undefined): Date => {
    if (!utcDateString) {
        return new Date();
    }
    const localDate = moment.utc(utcDateString).local();
    return localDate.isValid() ? localDate.toDate() : new Date();
};

export const getSelectedOption = (selectedValue: string | number, options: any[], matchWidth: string = "value") => {
    let selected: any = null;
    options?.map((option: any, i: number) => {
        if (option[matchWidth] == selectedValue) {
            if (!selected) selected = option;
        }
    });
    return selected;
};
export const getSelectedOptions = (selectedValues: string[] | number[] | null, options: any[]) => {
    const selected: any[] = [];
    selectedValues?.map((value) => {
        selected.push(getSelectedOption(value, options));
    });
    return selected;
};

// Builds and downloads a CSV file from headers + rows. Prepends a UTF-8 BOM
// so Excel (which ignores the Blob's charset=utf-8 MIME type and falls back
// to the system codepage) renders non-Latin1 text correctly instead of
// mojibake -- without it, e.g. Cyrillic "Продано единиц" round-trips as
// "ÐŸÑ€Ð¾Ð´Ð°Ð½Ð¾ ÐµÐ´Ð¸Ð½Ð¸Ñ†" when opened in Excel.
export const exportCsv = (
    headers: (string | number)[],
    rows: (string | number)[][],
    filename: string,
    includeBom: boolean = true
): void => {
    const escapeCsvValue = (value: string | number) => {
        const stringValue = String(value ?? "");
        if (
            stringValue.includes(",") ||
            stringValue.includes('"') ||
            stringValue.includes("\n")
        ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    };

    const csvContent = [
        headers.map(escapeCsvValue).join(","),
        ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");

    const blob = new Blob([(includeBom ? "﻿" : "") + csvContent], {
        type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const downloadExcel = (data: any, fileName: string) => {
    const blob = new Blob([data], { type: "application/vnd.ms-excel " });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
};
export const downloadPDF = (data: any, fileName: string) => {
    const blob = new Blob([data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName + ".pdf";
    a.click();
    window.URL.revokeObjectURL(url);
};
export const deleteItem = (item: any, itemType: string, onConfirm: Function, confirmDialog: any) => {
    // itemType is a plain English label passed by each call site (e.g. "Product",
    // "Brand", "Existing Product Image"); translated via a lookup table in the
    // shared `common` namespace, falling back to the raw string for any itemType
    // not yet cataloged there rather than leaving the whole dialog untranslated.
    const translatedType = i18n.t(`common:entityTypes.${itemType}`, { defaultValue: itemType });
    confirmDialog({
        type: "delete",
        title: i18n.t("common:deleteConfirm.title", { itemType: translatedType }),
        message: i18n.t("common:deleteConfirm.message", { itemType: translatedType, name: item.name }),
        onConfirm: () => onConfirm(item),
    });
};

export const navigationConfirm = (title: string, message: string, onConfirm: Function, confirmDialog: any) => {
    confirmDialog({
        type: "warn",
        title: title,
        message: message,
        onConfirm: () => onConfirm(),
    });
}

export const getInventoryCount = (levels: any[], fieldName: string) => {
    let sum = 0;
    levels?.map((level: any) => (sum += level[fieldName]));
    return sum;
};

export const getRetailPrices = (prices: any, fieldName: string) => {
    if (!prices || prices.length === 0) return `$0.00`;
    // console.log("Prices:", prices);
    const retailPrices = prices?.map((price: any) => price[fieldName]).filter((p: any) => typeof p === "number");
    if (retailPrices.length === 0) return `$0.00`;
    const min = Math.min(...retailPrices);
    const max = Math.max(...retailPrices);
    return min === max ? `$${formatCurrency(min)}` : `$${formatCurrency(min)} - $${formatCurrency(max)}`;
};

// Returns formatted cash price range, deriving from retailPriceExclTax when cashPriceExclTax is missing/zero.
export const getCashPriceDisplay = (prices: any, cashDiscountConfig: CashDiscountConfig): string => {
    if (!prices || prices.length === 0) return `$0.00`;
    const cashValues = prices.map((price: any) => {
        const stored = typeof price.cashPriceExclTax === 'number' ? price.cashPriceExclTax : parseFloat(price.cashPriceExclTax);
        if (!isNaN(stored) && stored > 0) return stored;
        // Fall back to deriving from retail using cashDiscount % whenever cashDiscount is configured
        if (cashDiscountConfig && cashDiscountConfig.cashDiscount > 0) {
            const retail = parseFloat(price.retailPriceExclTax) || 0;
            if (retail > 0) return (retail * 100) / (100 + cashDiscountConfig.cashDiscount);
        }
        return 0;
    }).filter((p: number) => p > 0);
    if (cashValues.length === 0) return `$0.00`;
    const min = Math.min(...cashValues);
    const max = Math.max(...cashValues);
    return min === max ? `$${formatCurrency(min)}` : `$${formatCurrency(min)} - $${formatCurrency(max)}`;
};

export const getCashPrices = (prices: any, fieldName: string, discountPercent: number) => {
    if (!prices || prices.length === 0) return `$0.00`;
    const cashPrices = prices
        ?.map((price: any) => price[fieldName])
        .filter((p: any) => typeof p === "number")
        .map((p: number) => (p * 100) / (100 + discountPercent));
    if (cashPrices.length === 0) return `$0.00`;
    const min = Math.min(...cashPrices);
    const max = Math.max(...cashPrices);
    return min === max ? `$${formatCurrency(min)}` : `$${formatCurrency(min)} - $${formatCurrency(max)}`;
};

export const getRetailPriceInArray = (prices: any, fieldName: string) => {
    if (!prices || prices.length === 0) return [0];
    const retailPrices = prices?.map((price: any) => price[fieldName]).filter((p: any) => typeof p === "number");
    if (retailPrices.length === 0) return [0];
    const min = Math.min(...retailPrices);
    const max = Math.max(...retailPrices);
    return min === max ? [min] : [min, max];
};

export const formatUSNumber = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined || value === '') {
        return '0.00';
    }

    // Convert to number
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    // Check if valid number
    if (isNaN(numValue)) {
        return '0.00';
    }

    // Format with US locale (adds commas) and exactly 2 decimal places
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numValue);
};

/**
 * Format US phone number to standard format
 * @param phoneNumber - Phone number string to format
 * @returns Formatted phone number or "-" if invalid
 */
export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
    if (!phoneNumber) return "-";
    
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if it's a valid US phone number length (10 or 11 digits)
    if (cleaned.length === 10) {
        // Format: (123) 456-7890
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
        // Format with country code: +1 (123) 456-7890
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    
    // Return original if it doesn't match expected format
    return phoneNumber;
};


// Format currency in US format with commas
// Round a monetary value to 2 decimals using half-up rounding.
// Plain Number.toFixed(2) / Math.round(n*100)/100 round 4.845 DOWN to 4.84 because
// 4.845 is stored as 4.84499999…; the EPSILON nudge restores the expected half-up result.
// Always round amounts through this BEFORE deriving dependent values (margin, cash price)
// so every field is computed from the same rounded base and stays internally consistent.
// export const roundMoney = (value: number): number => {
//     if (!isFinite(value)) return 0;
//     return Math.round((value + Number.EPSILON) * 100) / 100;
// };

export const roundMoney = (value: number): number => {     
    if (!isFinite(value)) return 0;     
    // Shift decimal right, round it, then shift it back left
    return Number(Math.round(+(value + 'e2')) + 'e-2');
};

export const formatCurrency = (value: number | undefined | null | string): string => {
    if (value === null || value === undefined) {
        return '0.00';
    }
    if (typeof value === 'string') {
        value = parseFloat(value);
        if (isNaN(value)) {
            return '0.00';
        }
    }
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Format quantity in US format with commas
export const formatQuantity = (value: number | undefined | null | string): string => {
    if (value === null || value === undefined) {
        return '0';
    }
    if (typeof value === 'string') {
        value = parseFloat(value);
        if (isNaN(value)) {
            return '0';
        }
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// Split an order's total discount amount into named lines (one per applied discount),
// falling back to a single generic "Discount" line when discount metadata isn't available.
// Lines with a zero/negative amount are omitted so no discount means no line is shown.
export const getOrderDiscountBreakdown = (
    orderDiscount: any[] | undefined | null,
    items: any[] | undefined | null,
    totalDiscountAmount: number
): { name: string; amount: number }[] => {
    const discounts = orderDiscount || [];
    let breakdown: { name: string; amount: number }[];
    if (discounts.length === 0) {
        breakdown = [{ name: 'Discount', amount: totalDiscountAmount || 0 }];
    } else {
        breakdown = discounts.map((discount) => {
            const productIds = new Set((discount.products || []).map((p: any) => p.fkProductId));
            const itemMappedAmount = (items || []).reduce((sum: number, item: any) => {
                return productIds.has(item.fkProductId) ? sum + (item.discountAmount || 0) : sum;
            }, 0);
            // Discounts tied to specific products are summed from item mapping;
            // order-level discounts (no product mapping) fall back to the order's discount amount.
            const amount = itemMappedAmount > 0 ? itemMappedAmount : (totalDiscountAmount || 0);
            return { name: discount.discountName ? `Discount ( ${discount.discountName} )` : 'Discount', amount };
        });
    }
    return breakdown.filter((discount) => discount.amount > 0);
}

// Convert 8-digit UPC-E to 12-digit UPC-A
export const convertUPCEtoUPCA = (upce: string): string | null => {
    if (!/^\d{8}$/.test(upce)) return null;
    const n = upce[0];
    const e = upce.slice(1, 7);
    const c = upce[7];
    const lastDigit = parseInt(e[5], 10);
    let middle: string;
    if (lastDigit <= 2) {
        middle = e[0] + e[1] + e[5] + "0000" + e[2] + e[3] + e[4];
    } else if (lastDigit === 3) {
        middle = e[0] + e[1] + e[2] + "00000" + e[3] + e[4];
    } else if (lastDigit === 4) {
        middle = e[0] + e[1] + e[2] + e[3] + "00000" + e[4];
    } else {
        middle = e[0] + e[1] + e[2] + e[3] + e[4] + "0000" + e[5];
    }
    return n + middle + c;
};

// Resolve alternateBarcode and additionalBarcode for payload submission.
// Scanner input arrives pre-converted (alternateBarcode=12-digit, additionalBarcode=original).
// Manual input arrives as typed (no real-time conversion).
export const resolveBarcodesForPayload = (
    alternateBarcode: string,
    additionalBarcode: string
): { alternateBarcode: string; additionalBarcode: string } => {
    const alt = alternateBarcode || "";
    const add = additionalBarcode || "";
    if (!alt) return { alternateBarcode: "", additionalBarcode: "" };
    // 8 digits exactly: UPC-E → UPC-A algorithm
    if (/^\d{8}$/.test(alt)) {
        const upca = convertUPCEtoUPCA(alt);
        return {
            alternateBarcode: upca || alt,
            additionalBarcode: alt,
        };
    }
    // 9–11 digits: pad to 12 with leading zeros
    if (/^\d+$/.test(alt) && alt.length >= 9 && alt.length < 12) {
        return {
            alternateBarcode: alt.padStart(12, '0'),
            additionalBarcode: alt,
        };
    }
    // 12-digit or other: store in both (additionalBarcode may already be set by scanner)
    return {
        alternateBarcode: alt,
        additionalBarcode: add || alt,
    };
};

export type CashDiscountConfig = {
    isCashDiscount: boolean;
    cashDiscount: number;
    isDualPricing: boolean;
    dualPriceDefault?: number | string | null;
} | null;

export type PricingMode = 'retail-only' | 'cash-discount' | 'dual-pricing';

export function getPricingMode(config: CashDiscountConfig): PricingMode {
    if (!config) return 'retail-only';
    if (config.isCashDiscount && !config.isDualPricing) return 'cash-discount';
    if (config.isDualPricing && !config.isCashDiscount) return 'dual-pricing';
    return 'retail-only';
}

// Returns 'card' if card price is the primary (editable) field,
// 'cash' if cash price is the primary (editable) field, or null if neither/not set.
export function resolveDualPriceDefaultType(
    dualPriceDefaultId: number | string | null | undefined,
    priceTypes: any[]
): 'card' | 'cash' | null {
    if (dualPriceDefaultId == null || dualPriceDefaultId === '' || !priceTypes?.length) return null;

    // Resolve to a numeric ID.
    // The API returns a base64-encoded string (e.g. "MzA5" → atob → "309" → 309).
    // Guard: also accept a bare numeric value so callers that stored the raw number still work.
    let numericId: number;
    if (typeof dualPriceDefaultId === 'string') {
        try {
            const decoded = atob(dualPriceDefaultId);
            numericId = parseInt(decoded, 10);
        } catch {
            // Not valid base64 – try treating it as a plain numeric string
            numericId = parseInt(dualPriceDefaultId, 10);
        }
        if (isNaN(numericId)) return null;
    } else {
        numericId = dualPriceDefaultId;
    }

    // Compare using Number() so the match works whether pt.lookupId is a string or number
    const item = priceTypes.find((pt: any) => Number(pt.lookupId) === numericId);
    if (!item) return null;

    const itemLookupId = Number(item.lookupId);
    if (itemLookupId === LOOKUP_VALUE.PRICE_TYPE_CARD) return 'card';
    if (itemLookupId === LOOKUP_VALUE.PRICE_TYPE_CASH) return 'cash';
    return null;
}

// Link encoded in the receipt QR code: the public Customer Feedback page for one order.
// Uses the environment's own app domain (REACT_APP_DOMAIN) rather than window.location,
// so a receipt printed from a local dev server still encodes a URL a customer's phone can open.
// `encodedOrderId` is the base64 order id, as the feedback page atob()s the `order` param.
export const getCustomerFeedbackUrl = (encodedOrderId: string): string =>
    `${(APP_DOMAIN || "").replace(/\/+$/, "")}${URLS.CUSTOMER_FEEDBACK}?order=${encodeURIComponent(encodedOrderId)}`;

// Generated locally so order data is never sent to a third-party QR service.
export const generateQrCodeDataUrl = (value: string): Promise<string> =>
    QRCode.toDataURL(value, { margin: 1, width: 200 });
